/**
 * Phase 14b-1-3 — `<AdminListFilters>` shared filter form.
 *
 * Each list page declares its filter shape as a discriminated union of
 * field configs. The component renders a horizontal form bar above the
 * table; on any field change it calls `onChange` with the merged values.
 *
 * URL-syncing is the page-level consumer's responsibility — use TanStack
 * Router's `useSearch` + `navigate({ search: ... })` pattern. Example:
 *
 *   const search = useSearch({ from: '/billing/invoices' });
 *   const navigate = useNavigate();
 *   <AdminListFilters
 *     fields={[
 *       { name: 'status', label: 'Status', type: 'select', options: [...] },
 *       { name: 'fromDate', label: 'From', type: 'date' },
 *       { name: 'q', label: 'Search', type: 'text', placeholder: 'invoice number' },
 *     ]}
 *     values={search}
 *     onChange={(next) => navigate({ search: { ...search, ...next, page: 1 } })}
 *   />
 *
 * Locked as extensible per 14b-1-3 — adding new variants (e.g.,
 * 'number-range', 'multi-select', 'autocomplete') at v1.x is additive +
 * non-breaking; existing usages compile unchanged.
 */

import { DateInput, Field, Input } from '@truecare/ui';

export type FilterField =
  | { name: string; label: string; type: 'text'; placeholder?: string }
  | { name: string; label: string; type: 'date' }
  | { name: string; label: string; type: 'select'; options: { value: string; label: string }[] };

export interface AdminListFiltersProps {
  fields: FilterField[];
  values: Record<string, string | undefined>;
  onChange: (values: Record<string, string | undefined>) => void;
}

export function AdminListFilters(props: AdminListFiltersProps) {
  const { fields, values, onChange } = props;

  function update(name: string, raw: string): void {
    // Empty string → undefined so the URL-sync drops the param rather
    // than carrying ?status= bare. Mirrors Phase 13e's filter normalisation.
    const next = raw === '' ? undefined : raw;
    onChange({ ...values, [name]: next });
  }

  return (
    <div
      className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-paper px-4 py-3"
      data-testid="admin-list-filters"
    >
      {fields.map((f) => {
        const value = values[f.name] ?? '';
        if (f.type === 'text') {
          return (
            <div key={f.name} className="min-w-[12rem] flex-1">
              <Field label={f.label}>
                <Input
                  value={value}
                  onChange={(e) => update(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`admin-list-filter-${f.name}`}
                />
              </Field>
            </div>
          );
        }
        if (f.type === 'date') {
          return (
            <div key={f.name} className="min-w-[10rem]">
              <Field label={f.label}>
                <DateInput
                  value={value}
                  onChange={(v) => update(f.name, v)}
                  data-testid={`admin-list-filter-${f.name}`}
                />
              </Field>
            </div>
          );
        }
        // select
        return (
          <div key={f.name} className="min-w-[10rem]">
            <Field label={f.label}>
              <select
                className="w-full rounded-sm border border-border bg-white px-2 py-2 text-forest"
                value={value}
                onChange={(e) => update(f.name, e.target.value)}
                data-testid={`admin-list-filter-${f.name}`}
              >
                <option value="">All</option>
                {f.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        );
      })}
    </div>
  );
}
